import { RootState } from "../store"

/**
 * Per-organization rules layered on top of a base sharing ministry plan,
 * stored in the `organizations.customizations` JSONB column.
 *
 * Ministry identity and tier data (sharing ministry, Sedera IUA) are NOT
 * customizations — they live on the membership/organization rows and are
 * selected via `selectors/sharingMinistry.ts`.
 */
export interface Customizations {
  hra?: HraCustomizations,

  advances?: AdvanceCustomizations

  personalResponsibility?: PersonalResponsibilityCustomizations
}

export type HraCustomizations = {
  hasHra?: false | undefined,
  hraUrl?: undefined,
  providesHraCard?: false | undefined,
  hraCardLabel?: undefined
} | {
  /**
   * Whether the organization provides an HRA for the member
   */
  hasHra: true,
  /**
   * The URL for submitting an HRA
   */
  hraUrl: string,
  
  /**
   * Whether the organization provides an HRA card
   */
  providesHraCard: boolean
  
  /**
   * If they refer to the card as something else like "Blue Card", this can be used to customize the label
   */
  hraCardLabel?: string
}

export type PersonalResponsibilityCustomizations = {
  reimbursesChmPersonalResponsibility?: false | undefined
  reimbursementUrl?: undefined
  maternalPersonalResponsibilityReimbursedAmount?: undefined
  personalResponsibilityInstructionsMarkdown?: undefined
} | {
  /**
   * Whether the organization reimburses the member for their CHM personal responsibility
   */
  reimbursesChmPersonalResponsibility: true
  /**
   * The URL to show in the checklist
   */
  reimbursementUrl: string

  /**
   * The maximum amount of maternity personal responsibility that is actually reimbursed by the organization
   */
  maternalPersonalResponsibilityReimbursedAmount?: number
  
  /**
   * Custom markdown instructions for personal responsibility submissions. If not provided, falls back to default instructions.
   */
  personalResponsibilityInstructionsMarkdown?: string
}

export const DefaultCustomizations: Customizations = {
  hra: {
    hasHra: false,
  },
  
  personalResponsibility: {
    reimbursesChmPersonalResponsibility: false
  }
}

export function selectCustomizations(s: RootState): Customizations {
  const membershipCustomization = (s.membership?.organization?.customizations as unknown as Partial<Customizations>) ?? {}

  return {
    ...DefaultCustomizations,
    ...membershipCustomization,
  }
}

/**
 * Expense forms offer "paid with the HRA card" only when the organization's
 * `hra.providesHraCard` customization is true.
 *
 * An organization with no HRA, or an HRA that does not include a card, does
 * not get the choice. This is the check the web expense forms use.
 */
export function organizationProvidesHraCard(hra: HraCustomizations | null | undefined): boolean {
  return hra?.providesHraCard === true
}

/**
 * Member-facing name of the organization's HRA card.
 *
 * Web expense forms use `hraCardLabel || 'HRA card'`. The expense-row badge
 * passes `'HRA CARD'` so a custom name stays as written and the default stays
 * uppercase.
 */
export function hraCardDisplayLabel(
  hra: HraCustomizations | null | undefined,
  fallback = 'HRA card',
): string {
  return hra?.hraCardLabel || fallback
}

export type AdvanceCustomizations = {
  advanceLimit?: undefined
} | {
  advanceLimit: string
}

